import React from 'react';

import { InteractionDialog } from '../InteractionDialog';
import { AllocationDialog } from './panels/InteractionPanel/AllocationDialog';
import { CheckInDialog } from './panels/InteractionPanel/CheckInDialog';
import { PartActionDialog } from './panels/InteractionPanel/PartActionDialog';
import { ReopenJobDialog } from './panels/InteractionPanel/ReopenJobDialog';
import { StatusChangeDialog } from './panels/InteractionPanel/StatusChangeDialog';
import { TaskDialog } from './panels/InteractionPanel/TaskDialog';
import { useServiceDialogs } from './useServiceDialogs';
import { ActionPermissions, CustomPermission } from '../../../@types';
import { ServiceObject } from '../../../@types/service.type';
import { isUserHasPermission } from '../../../configs/permissions';
import { getDateFromTimestamp } from '../../../utils/common';

export interface ServiceDetailDialogsProps {
  service: ServiceObject | null;
  serviceId: number;
  dialogs: ReturnType<typeof useServiceDialogs>;
  permissions?: CustomPermission[];
}

/**
 * Bundles every dialog launched from ServiceDetail so the page component
 * doesn't have to wire each one inline. State stays in `useServiceDialogs`
 * and the parent passes it through verbatim.
 */
export const ServiceDetailDialogs: React.FC<ServiceDetailDialogsProps> = ({
  service,
  serviceId,
  dialogs,
  permissions,
}) => (
  <>
    <StatusChangeDialog
      open={dialogs.statusDialogOpen}
      onClose={dialogs.handleCloseStatusDialog}
      currentStatus={service?.serviceStatus?.name ?? ''}
      possibleStatuses={dialogs.nextStatusesQuery.data?.possibleNextStatuses ?? []}
      preferredStatuses={dialogs.nextStatusesQuery.data?.preferedStatuses ?? []}
      loading={dialogs.nextStatusesQuery.isFetching}
      onSubmit={dialogs.handleStatusSubmit}
      submitting={dialogs.statusSubmitting}
    />
    <AllocationDialog
      open={dialogs.allocationDialogOpen}
      onClose={dialogs.handleCloseAllocationDialog}
      currentTechnician={service?.primaryAllocation?.node?.name ?? ''}
      onSubmit={dialogs.handleAllocationSubmit}
      submitting={dialogs.allocationSubmitting}
    />
    <PartActionDialog
      open={dialogs.partActionDialogOpen}
      onClose={dialogs.handleClosePartActionDialog}
      serviceId={serviceId}
      clientId={service?.clientId ?? 0}
      contractId={service?.contractId ?? 0}
      locationId={service?.mainLocationNodeId ?? 0}
      serviceTypeCode={service?.serviceTypeCode ?? ''}
      reportedSerialNumberPartId={service?.reportedSerialNumberPartId || undefined}
      canCreateExceptional={isUserHasPermission(
        ActionPermissions.Service_Edit_Part_Actions_Exceptional_Creation,
        permissions
      )}
      onSubmit={dialogs.handlePartActionSubmit}
      submitting={dialogs.partActionSubmitting}
    />
    <TaskDialog
      open={dialogs.taskDialogOpen}
      onClose={dialogs.handleCloseTaskDialog}
      serviceId={serviceId}
      taskTypes={dialogs.taskTypesQuery.data ?? []}
      taskTypesLoading={dialogs.taskTypesQuery.isFetching}
      onSubmit={dialogs.handleTaskSubmit}
      submitting={dialogs.taskSubmitting}
    />
    <StatusChangeDialog
      open={dialogs.cancelFutileDialogOpen}
      onClose={dialogs.handleCloseCancelFutileDialog}
      currentStatus={service?.serviceStatus?.name ?? ''}
      possibleStatuses={dialogs.cancelFutileStatusesQuery.data ?? []}
      preferredStatuses={[]}
      loading={dialogs.cancelFutileStatusesQuery.isFetching}
      onSubmit={dialogs.handleCancelFutileSubmit}
      submitting={dialogs.statusSubmitting}
    />
    <ReopenJobDialog
      open={dialogs.reopenDialogOpen}
      onClose={dialogs.handleCloseReopenDialog}
      currentStatus={service?.serviceStatus?.name ?? ''}
      reopenStatuses={dialogs.reopenStatusesQuery.data ?? []}
      loading={dialogs.reopenStatusesQuery.isFetching}
      onSubmit={dialogs.handleReopenSubmit}
      submitting={dialogs.statusSubmitting}
    />
    <CheckInDialog
      open={dialogs.checkInDialogOpen}
      onClose={dialogs.handleCloseCheckInDialog}
      onSubmit={dialogs.handleCheckInSubmit}
      submitting={dialogs.checkInSubmitting}
      travelStartTime={
        service?.travelStartTime ? getDateFromTimestamp(service.travelStartTime) : ''
      }
    />
    <InteractionDialog
      interactions={dialogs.pendingInteractions}
      permissions={permissions}
      onComplete={dialogs.clearPendingInteractions}
      onCancel={dialogs.cancelPendingInteractions}
    />
  </>
);
